/**
 * phi-scrub.ts — PHI scrubbing for Sentry events (runtime-agnostic).
 *
 * Pure functions with a single dependency (@nrx/audit/phi-fields), so this module
 * is safe to import in BOTH Node (services) and browser (React apps) bundles: it
 * pulls no @sentry/*, no aws-sdk, and no node:async_hooks. The Node entry
 * (sentry.ts) and the browser entry (@nrx/error-tracking-web) both wire these
 * scrubbers into their Sentry `beforeSend` hook.
 *
 * These run in `beforeSend`, which the SDK gates on ERROR events. A sampled TRANSACTION goes to
 * `beforeSendTransaction` and nothing registers one, so its request headers, cookies and URL reach
 * the SaaS unscrubbed (#1180). `sentry.ts` suppresses only the request BODY, at the source.
 */

import { PHI_FIELD_DENYLIST } from '@nrx/audit/phi-fields';

// Sentry Event shape — a subset of the real Sentry.Event interface, kept here so
// this module needs no @sentry/* dependency.
export interface SentryExceptionValue {
  type?: string | undefined;
  value?: string | undefined;
  [key: string]: unknown;
}

export interface SentryException {
  values?: SentryExceptionValue[] | undefined;
  [key: string]: unknown;
}

/** `httpContextIntegration` fills this in the browser, `requestDataIntegration` in Node. */
export interface SentryRequest {
  url?: string | undefined;
  query_string?: unknown;
  headers?: Record<string, unknown> | undefined;
  [key: string]: unknown;
}

/** A Sentry breadcrumb. Navigation crumbs carry `data.from`/`data.to`; fetch/xhr carry `data.url`. */
export interface SentryBreadcrumb {
  message?: string | undefined;
  data?: Record<string, unknown> | undefined;
  [key: string]: unknown;
}

/**
 * A request the projection has run over — branded so a raw one cannot be passed where a projected
 * one belongs. The brand is type-only; no such property exists at runtime.
 */
export type ScrubbedRequest = SentryRequest & { readonly '~scrubbed': 'request' };

export type ScrubbedBreadcrumb = SentryBreadcrumb & { readonly '~scrubbed': 'breadcrumb' };

export interface SentryEvent {
  message?: string | undefined;
  /** The event title, and transmitted. */
  transaction?: string | undefined;
  extra?: Record<string, unknown> | undefined;
  user?: Record<string, unknown> | undefined;
  contexts?: Record<string, Record<string, unknown>> | undefined;
  exception?: SentryException | undefined;
  request?: SentryRequest | undefined;
  breadcrumbs?: SentryBreadcrumb[] | undefined;
  [key: string]: unknown;
}

export interface ScrubbedEvent extends SentryEvent {
  request?: ScrubbedRequest | undefined;
  breadcrumbs?: ScrubbedBreadcrumb[] | undefined;
}

const phiSet = new Set<string>(PHI_FIELD_DENYLIST);

/**
 * Regex patterns for PHI that may appear inline in exception value strings.
 * Names have no reliable shape; those stay on the keyed denylist only.
 *
 * MBI note: The strict CMS MBI format has specific character class rules per position.
 * For exception scrubbing we use a broader 11-character pattern starting with a non-zero
 * digit followed by alphanumerics, which reliably matches MBI-formatted identifiers
 * in error message context while avoiding false positives on common numeric tokens.
 */
const PHI_STRING_PATTERNS: RegExp[] = [
  // MBI (Medicare Beneficiary Identifier): 11 alphanumeric chars, starts with [1-9],
  // followed by exactly 10 uppercase alpha/digit characters. This broad pattern covers
  // both strict CMS format and test/example MBIs that appear in exception messages.
  /\b[1-9][A-Z0-9]{10}\b/g,
  // SSN: 000-00-0000
  /\b\d{3}-\d{2}-\d{4}\b/g,
  // Date of birth patterns: YYYY-MM-DD or MM/DD/YYYY
  /\b(?:19|20)\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])\b/g,
  /\b(?:0[1-9]|1[0-2])\/(?:0[1-9]|[12]\d|3[01])\/(?:19|20)\d{2}\b/g,
  // Invite token: 64-char lowercase hex string (PHI-adjacent credential).
  // A raw invite token grants access to an enrollment session — treat as PHI.
  /\b[0-9a-f]{64}\b/gi,
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
  // NANP with separators so this does not collide with SSN (3-2-4) or NPI (10 digits).
  /(?:\+1[-.\s]*)?(?:\(?\d{3}\)?[-.\s]*)\d{3}[-.\s]\d{4}\b/g,
  // NPI, and unformatted 10-digit phone. Fail-closed: other 10-digit tokens redact too.
  /\b\d{10}\b/g,
];

/**
 * Scrubs PHI patterns from a free-text string (exception value).
 */
function scrubString(value: string): string {
  let scrubbed = value;
  for (const pattern of PHI_STRING_PATTERNS) {
    // Reset lastIndex for global regexes between calls
    pattern.lastIndex = 0;
    scrubbed = scrubbed.replace(pattern, '[REDACTED]');
  }
  return scrubbed;
}

/**
 * Scrubs top-level PHI fields from an object: values whose keys match the
 * denylist are replaced with '[REDACTED]'; the key is preserved.
 */
export function scrubObject(
  obj: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (obj === undefined || obj === null) return obj;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key] = phiSet.has(key) ? '[REDACTED]' : value;
  }
  return result;
}

/** The SDK's own text for a captured plain object: it lists property NAMES, never values. */
const OBJECT_CAPTURE_VALUE = /^Object captured as (?:exception|promise rejection) with keys: /;
/** The SDK's prefix for a rejection whose reason is a primitive; the primitive follows it. */
const PRIMITIVE_REJECTION_PREFIX = 'Non-Error promise rejection captured with value: ';

/**
 * The value a non-`Error` capture carries, or null when this exception came from a real `Error`.
 *
 * A thrown or rejected non-`Error` has no authored message: the SDK makes one out of the value
 * itself (`synthetic: true` for a direct capture; the rejection prefix for an unhandled one). So
 * that value is caller data, and the pattern scrub is not enough for it — a name or an address has
 * no shape a pattern can match. It is destroyed, keeping only the SDK's own words.
 */
function redactNonErrorValue(exVal: SentryExceptionValue, value: string): string | null {
  if (value.startsWith(PRIMITIVE_REJECTION_PREFIX)) {
    return PRIMITIVE_REJECTION_PREFIX + REDACTED;
  }
  const mechanism = exVal['mechanism'] as { synthetic?: unknown } | undefined;
  if (mechanism?.synthetic !== true) return null;
  return OBJECT_CAPTURE_VALUE.test(value) ? value : REDACTED;
}

/**
 * Scrubs PHI from Sentry exception values (free-text error message strings): a non-`Error`
 * capture's value is destroyed (see `redactNonErrorValue`), anything else is pattern-scrubbed.
 */
function scrubExceptionValues(exception: SentryException | undefined): SentryException | undefined {
  if (exception === undefined || exception === null) return exception;
  if (!Array.isArray(exception.values)) return exception;

  return {
    ...exception,
    values: exception.values.map((exVal) => {
      if (exVal.value === undefined || exVal.value === null || typeof exVal.value !== 'string') {
        return exVal;
      }
      return {
        ...exVal,
        value: redactNonErrorValue(exVal, exVal.value) ?? scrubString(exVal.value),
      };
    }),
  };
}

/**
 * `extra` with its denylisted keys redacted and `__serialized__` destroyed.
 *
 * `__serialized__` is where the SDK puts a captured non-`Error` object, normalised whole. The
 * denylist checks top-level keys only, so everything nested under it used to go out as sent —
 * a `{ patient: { name } }` rejection arrived intact. It is caller data with no authored shape.
 */
function scrubExtra(
  extra: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  const scrubbed = scrubObject(extra);
  if (scrubbed !== undefined && scrubbed !== null && '__serialized__' in scrubbed) {
    scrubbed['__serialized__'] = REDACTED;
  }
  return scrubbed;
}

/**
 * A path segment long and opaque enough to be a credential rather than a route name.
 *
 * ONE RULE, NOT TWO. An earlier revision also carried an explicit UUID pattern — the shape
 * `randomUUID()` actually mints (`mint-scheduling-token.use-case.ts`) — until mutation testing
 * showed that deleting it changed no test: a UUID is 36 characters of `[A-Za-z0-9-]`, so this rule
 * already catches every one. Two patterns implied two independent defences where there was one, so
 * the redundant one is gone. This also covers the opaque, non-UUID tokens minted by the VENDOR
 * service whose rows the consult cutover copies, whose format this repo never chose.
 *
 * 20 IS CHECKED, NOT GUESSED — for the BROWSER: the longest segment the React apps declare is 18
 * characters, and `phi-scrub.spec.ts` pins that against the tree, so a 20-character route fails
 * there rather than silently degrading every Sentry URL for that app. NODE SERVICES ARE NOT
 * COVERED: ten admin routes (`nutritional-target-profiles`, ...) are 20+ characters and redact. A
 * real diagnostic loss, accepted — a 16-character opaque token is likelier than a 30-character
 * route, so lowering the floor is the worse trade.
 *
 * NOT THE ONLY PATH RULE: ROUTE_NAME_SEGMENT below destroys every segment that is not a route
 * name, catching short opaque tokens. This one still earns its place because a long lowercase
 * token (a 64-char hex invite token, a base32 id) IS route-shaped.
 */
const OPAQUE_CREDENTIAL_SEGMENT = /^[A-Za-z0-9_-]{20,}$/;

/**
 * A path segment that is a route NAME rather than data. `phi-scrub.spec.ts` crawls the routes
 * declared in `apps/` and in the service controllers and asserts none is redacted, so a route this
 * does not admit fails there rather than going dark in Sentry.
 *
 * AN ALLOWLIST, because no denylist reaches this: the member identifiers on live routes
 * (`npi/:npi`, `:memberId/confirm-ineligible`) are under the 20-character opaque-credential floor,
 * a name has no shape to pattern-match, and `%40` hides an email from the email pattern.
 *
 * IT COSTS `/items/42` and `/favicon.ico`, which redact — deliberate: a bare number in a path is
 * an account number as readily as a page number.
 *
 * WHAT IT CANNOT SEE: lowercase free text, digits included — `/members/john-smith-1978` is a Safe
 * Harbor name and a birth year, two HIPAA identifiers, in one segment still shaped like a route.
 * Excluding digit-bearing groups would cost live routes (`v1`, `icd10`) and still leave free text
 * that has no shape.
 */
const ROUTE_NAME_SEGMENT = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

/** '' is what a leading, trailing or doubled slash splits into — the slash itself, not a segment. */
function isRouteNameSegment(segment: string): boolean {
  if (segment === '') return true;
  return ROUTE_NAME_SEGMENT.test(segment) && !OPAQUE_CREDENTIAL_SEGMENT.test(segment);
}

const REDACTED = '[REDACTED]';

/**
 * Redacts credentials from a URL while preserving its diagnostic shape.
 *
 * WHY THIS EXISTS. For an unauthenticated magic-link app the URL *is* the credential:
 * `https://consult.<apex>/consult-scheduling/<TOKEN>` exchanges for a JWT returning member email,
 * first name, last name, phone and state. Sentry's `httpContextIntegration` is a DEFAULT
 * integration and sets `event.request.url = window.location.href` unconditionally, and
 * `breadcrumbsIntegration` records navigation and fetch URLs — so the credential left the browser
 * on every captured event, including the exchange-failure path that reports every expired link.
 * The scrubbers above never looked at `request` or `breadcrumbs`, so none of them saw it.
 *
 * WHAT IS KEPT. Origin, path structure and query-parameter NAMES all survive, because those are
 * what makes an error report worth having. Only values are destroyed:
 *   - EVERY query-parameter value, unconditionally. `?token=` is the credential channel in
 *     apps/enroll and apps/access-landing, and an allowlist here would be a list of the parameter
 *     names someone remembered.
 *   - Every path segment that is not a route NAME (ROUTE_NAME_SEGMENT), and every route-shaped one
 *     long enough to be an opaque credential (OPAQUE_CREDENTIAL_SEGMENT — a UUID satisfies it).
 *   - The fragment, entirely — `#access_token=` is the implicit-flow shape, and a fragment has no
 *     structure worth preserving.
 *
 * FAILS CLOSED. An unparseable value is replaced wholesale rather than returned as-is: the reason
 * it did not parse is unknown, so its contents are unknown.
 *
 * Accepts a PATH as well as an absolute URL — react-router navigation breadcrumbs carry
 * `data.to` / `data.from` as paths, which is exactly where the consult token appears.
 */
export function redactCredentialsFromUrl(value: string): string {
  if (value === '') return value;

  // A sentinel base lets one code path handle both absolute URLs and bare paths. `.invalid` is
  // reserved by RFC 2606, so it can never collide with a real origin we might be asked to keep.
  const SENTINEL = 'http://sentinel.invalid';
  let parsed: URL;
  try {
    parsed = new URL(value, SENTINEL);
  } catch {
    return REDACTED;
  }

  const wasRelative = !/^https?:\/\//i.test(value);
  // ABSOLUTE http(s), OR ROOTED. Everything else is refused, and this single check is what makes
  // an explicit protocol allowlist unnecessary: `new URL` does NOT throw on 'javascript:alert(1)'
  // or 'mailto:a@b.com' (those are valid URLs with their own schemes), but neither begins with
  // 'http://' nor with '/', so both land here. An earlier revision ALSO tested
  // `parsed.protocol`; mutation testing showed removing it changed no test, because this check
  // already subsumes it — a value starting with '/' is always resolved as http via the sentinel.
  // The behaviour is pinned by the 'fails CLOSED' test rather than by the redundant guard.
  if (wasRelative && !value.startsWith('/')) return REDACTED;

  const path = parsed.pathname
    .split('/')
    .map((segment) => (isRouteNameSegment(segment) ? segment : REDACTED))
    .join('/');

  // Rebuilt by hand rather than via URLSearchParams.set + parsed.toString(): the latter
  // percent-encodes the brackets in '[REDACTED]' into '%5BREDACTED%5D', which reads as data rather
  // than as a redaction marker in the Sentry UI.
  const names = [...parsed.searchParams.keys()];
  const query = names.length > 0 ? `?${names.map((n) => `${n}=${REDACTED}`).join('&')}` : '';
  const fragment = parsed.hash === '' ? '' : `#${REDACTED}`;

  return `${wasRelative ? '' : parsed.origin}${path}${query}${fragment}`;
}

/**
 * The SDK names an HTTP event from the framework's route template where one exists and from the
 * RAW request path where none does, so on a non-Nest entry point the title reads
 * `POST /v1/members/1EG4TE5MK72/uploads`.
 *
 * IT COSTS `:memberId`, which is not a route name and is destroyed with the real identifiers.
 * Grouping is unaffected: the redaction is stable per route.
 */
function redactTransactionName(value: string): string {
  const space = value.indexOf(' ');
  const verb = space === -1 ? '' : value.slice(0, space);
  if (/^[A-Z]+$/.test(verb)) return `${verb} ${redactCredentialsFromUrl(value.slice(space + 1))}`;
  if (value.startsWith('/')) return redactCredentialsFromUrl(value);
  return scrubString(value);
}

/** Redacts a value in place when it is a string, leaving every other type untouched. */
function redactUrlValue(value: unknown): unknown {
  return typeof value === 'string' ? redactCredentialsFromUrl(value) : value;
}

/**
 * Breadcrumb `data` keys that carry a URL. Exported so the browser entry, which redacts the same
 * keys at capture time, does not keep a second copy that can disagree.
 */
export const URL_DATA_KEYS: ReadonlySet<string> = new Set(['url', 'to', 'from']);

/**
 * Request headers that echo the full URL, and therefore the credential in it. Matched
 * case-insensitively: `KEPT_HEADER_VALUES` keeps these by lowercased name, so a spelling missed
 * here is kept verbatim — and only Node normalises header casing.
 */
const URL_HEADER_NAMES: ReadonlySet<string> = new Set(['referer', 'referrer']);

/**
 * Redacts credentials from the two event surfaces that carry URLs. Split out from the field
 * scrubbers above because those work by KEY NAME against a PHI denylist, and a URL leaks through a
 * key (`url`) that no denylist would ever list.
 */
export function scrubUrlsFromSentryEvent(event: SentryEvent): SentryEvent {
  const scrubbed: SentryEvent = { ...event };

  if (event.request !== undefined && event.request !== null) {
    const request: SentryRequest = { ...event.request };
    if (request.url !== undefined) request.url = redactCredentialsFromUrl(String(request.url));
    // The query string as its own field, which Sentry may populate independently of `url`.
    if (typeof request.query_string === 'string' && request.query_string !== '') {
      request.query_string = redactCredentialsFromUrl(`/?${request.query_string}`).replace(
        /^\/\?/,
        '',
      );
    }
    if (request.headers !== undefined && request.headers !== null) {
      const headers: Record<string, unknown> = {};
      for (const [name, value] of Object.entries(request.headers)) {
        headers[name] = URL_HEADER_NAMES.has(name.toLowerCase()) ? redactUrlValue(value) : value;
      }
      request.headers = headers;
    }
    scrubbed.request = request;
  }

  if (Array.isArray(event.breadcrumbs)) {
    scrubbed.breadcrumbs = event.breadcrumbs.map((crumb) => {
      // A non-object `data` is left whole for `scrubBreadcrumb`: spreading a string here shatters
      // it into an index-keyed bag, redacted one character per key.
      if (
        crumb === null ||
        crumb === undefined ||
        crumb.data === null ||
        typeof crumb.data !== 'object'
      ) {
        return crumb;
      }
      const data: Record<string, unknown> = { ...crumb.data };
      for (const key of URL_DATA_KEYS) {
        if (key in data) data[key] = redactUrlValue(data[key]);
      }
      return { ...crumb, data };
    });
  }

  return scrubbed;
}

/**
 * Header names whose VALUE survives. Every other header keeps its name and loses its value.
 *
 * AN ALLOWLIST, BECAUSE THE KEY SET IS NOT OURS: at `@sentry/core@10.59.0`
 * `extractNormalizedRequestData` copies the WHOLE incoming header bag onto the event, and the deny
 * rules `getDataCollectionOptions()` resolves apply only on the span path. `authorization` is the
 * name a denylist would remember; an `x-member-mbi` a partner invents is the one it would miss.
 *
 * Referer/Referrer survive because `scrubUrlsFromSentryEvent` has already redacted their value.
 */
const KEPT_HEADER_VALUES: ReadonlySet<string> = new Set([
  'accept',
  'accept-encoding',
  'accept-language',
  'baggage',
  'content-length',
  'content-type',
  'host',
  'origin',
  'referer',
  'referrer',
  'sentry-trace',
  'traceparent',
  'tracestate',
  'user-agent',
  'x-correlation-id',
]);

/**
 * Destroys every value in a name/value bag, except entries `keep` admits. A non-object fails
 * closed.
 */
function redactBagValues(bag: unknown, keep?: (name: string, value: unknown) => boolean): unknown {
  if (typeof bag !== 'object' || bag === null) return REDACTED;
  return Object.fromEntries(
    Object.entries(bag as Record<string, unknown>).map(([name, value]) => [
      name,
      keep?.(name, value) === true ? value : REDACTED,
    ]),
  );
}

/**
 * Projects `event.request`: the keys named here keep their value, every other key loses it.
 *
 * FAIL-CLOSED, unlike the denylist scrubbers above: they enumerate what to remove, so a key nobody
 * listed survives. `data` is why that matters — `resolveIncludeAndDataCollection` hard-codes
 * `data: true` at @sentry/core@10.59.0, so the raw request body reaches the event whatever
 * `sendDefaultPii` says, and on this fleet a request body is member fields by default.
 *
 * IT COSTS the body and the cookie jar on the next incident report. Deliberate: no shallower rule
 * separates a member payload from a benign one.
 *
 * Must run AFTER `scrubUrlsFromSentryEvent`, which is what makes `url` and a string `query_string`
 * safe to keep.
 */
function scrubRequestFromSentryEvent(request: SentryRequest): ScrubbedRequest {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(request)) {
    switch (key) {
      case 'url':
      case 'method':
        result[key] = value;
        break;
      // `QueryParams` is `string | Record<string, string> | Array<[string, string]>`. Both pinned
      // cores emit only the string, already value-stripped by `scrubUrlsFromSentryEvent`; the
      // other two shapes would reach here with their values intact.
      case 'query_string':
        result[key] = typeof value === 'string' ? value : REDACTED;
        break;
      case 'headers':
        result[key] = redactBagValues(value, (name) => KEPT_HEADER_VALUES.has(name.toLowerCase()));
        break;
      case 'cookies':
      case 'env':
        result[key] = redactBagValues(value);
        break;
      default:
        result[key] = REDACTED;
    }
  }
  return result as ScrubbedRequest;
}

/**
 * Breadcrumb `data` keys whose VALUE survives, and only when it holds a primitive: `data` is `any`,
 * so even `url` can arrive as an object.
 *
 * AN ALLOWLIST, BECAUSE THE KEY SET IS NOT OURS: `Breadcrumb.data` is `{ [key: string]: any }` at
 * both installed cores and application code adds keys through `Sentry.addBreadcrumb`, so there is
 * nothing to enumerate. `url`/`to`/`from` are safe here only because `scrubUrlsFromSentryEvent` has
 * already redacted them. Deliberately absent: the console crumb's raw `arguments`, and
 * `http.query`/`http.fragment`, which the http crumb splits out of the URL past that redaction.
 */
const KEPT_BREADCRUMB_DATA_KEYS: ReadonlySet<string> = new Set([
  ...URL_DATA_KEYS,
  'method',
  'http.method',
  'status_code',
  'logger',
]);

function isPrimitive(value: unknown): boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/**
 * The whole of the vendor's `Breadcrumb` interface at both installed cores, which carries no index
 * signature — so only a caller who casts can add a key, and `addBreadcrumb` merges whatever it is
 * given. Projecting against the closed set is what keeps that off the wire.
 */
const KEPT_BREADCRUMB_KEYS: ReadonlySet<string> = new Set([
  'type',
  'level',
  'event_id',
  'category',
  'message',
  'data',
  'timestamp',
]);

/**
 * `message` IS DESTROYED WHOLESALE rather than pattern-scrubbed: a name has no shape `scrubString`,
 * the rule used on exception values, can find. Nor would an allowlist of "safe" categories help —
 * at both installed versions every message-bearing crumb interpolates something it did not author.
 *
 * IT COSTS THE LOG TRAIL an incident is read with: console lines under a Sentry issue become a
 * column of `[REDACTED]`, leaving only the trail's shape. On the Node fleet the text is still in
 * CloudWatch under the same correlationId; in the browser it is gone.
 */
function scrubBreadcrumb(crumb: SentryBreadcrumb): ScrubbedBreadcrumb {
  // Emptied rather than thrown on: a throw out of `beforeSend` loses the whole report as well as
  // the defect that produced it.
  if (typeof crumb !== 'object' || crumb === null) return {} as ScrubbedBreadcrumb;
  const result: Record<string, unknown> = Object.fromEntries(
    Object.entries(crumb).filter(([key]) => KEPT_BREADCRUMB_KEYS.has(key)),
  );
  if (crumb.message !== undefined) result['message'] = REDACTED;
  if (crumb.data !== undefined) {
    result['data'] = redactBagValues(
      crumb.data,
      (name, value) => KEPT_BREADCRUMB_DATA_KEYS.has(name) && isPrimitive(value),
    );
  }
  return result as ScrubbedBreadcrumb;
}

/**
 * The `beforeSend` chokepoint both runtimes register. Returns null for a null/undefined event,
 * which is how Sentry is told to drop it.
 */
export function scrubPhiFromSentryEvent(
  event: SentryEvent | null | undefined,
): ScrubbedEvent | null {
  if (event === null || event === undefined) return null;

  // URLs first, so the field scrubbers below operate on an event whose credential-bearing
  // surfaces are already clean. Applied HERE rather than only in the browser entry so the single
  // beforeSend both runtimes already share closes every caller at once — the browser leak is the
  // acute one (the URL is the credential), but a service that puts a request URL on an event has
  // the same exposure.
  const { request, breadcrumbs, ...urlSafe } = scrubUrlsFromSentryEvent(event);

  return {
    ...urlSafe,
    ...(request !== undefined &&
      request !== null && { request: scrubRequestFromSentryEvent(request) }),
    // Destructured out above, so a non-array `breadcrumbs` is dropped rather than forwarded.
    ...(Array.isArray(breadcrumbs) && { breadcrumbs: breadcrumbs.map(scrubBreadcrumb) }),
    ...(urlSafe.transaction !== undefined && {
      transaction:
        typeof urlSafe.transaction === 'string'
          ? redactTransactionName(urlSafe.transaction)
          : REDACTED,
    }),
    ...(event.extra !== undefined && { extra: scrubExtra(event.extra) }),
    ...(event.user !== undefined && {
      user: scrubObject(event.user),
    }),
    ...(event.contexts !== undefined && {
      contexts: Object.fromEntries(
        Object.entries(event.contexts).map(([k, v]) => [k, scrubObject(v) ?? {}]),
      ),
    }),
    ...(event.exception !== undefined && {
      exception: scrubExceptionValues(event.exception),
    }),
  };
}
